import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Phone, AlertTriangle,
  Thermometer, Wind, PanelsTopLeft, Ruler,
  Sun, AirVent, ArrowDownToLine, FileCheck,
  Waves, Shield, Hammer, Droplets,
  SunMedium, Lightbulb, Wrench,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, heroImage, roofline, warranty } from "../data/site";

const icons = {
  Thermometer, Wind, PanelsTopLeft, Ruler,
  Sun, AirVent, ArrowDownToLine, FileCheck,
  Waves, Shield, Hammer, Droplets,
  SunMedium, Lightbulb, AlertTriangle, Wrench,
};

const FAQ = [
  {
    q: "Why do this with the roof instead of later?",
    a: "Because the access is already paid for. The deck is open, the crew is up there, the equipment is staged and the dumpster is in the driveway. Insulation, ventilation, gutters and skylights done during a re-roof cost a fraction of what the same work costs as its own job with its own mobilisation.",
  },
  {
    q: "How much insulation should my attic have?",
    a: `Under the 2021 energy code, this climate zone calls for ${roofline.codeRValue} in the ceiling — or ${roofline.coolRoofRValue} where a qualifying cool roof is in place. Which edition applies to your job is your city's call, not ours, and we will tell you what your jurisdiction has adopted. What we see most often on Valley homes built before the mid-2000s is well under either number.`,
  },
  {
    q: "Do solar attic vents need an electrician?",
    a: "Not the self-contained ones. The panel, the motor and the wiring between them are all part of the unit, and none of it touches your electrical panel or your premises wiring — so it installs as a roof penetration, flashed like any other vent. A hardwired powered fan is a different thing: that connection is electrical work and goes to a licensed electrical subcontractor.",
  },
  {
    q: "Will a solar vent lower my power bill?",
    a: "It can take the peak off an attic and ease the load on an air conditioner working against a 150-degree ceiling, which is worth something in July. Anyone quoting you a specific dollar figure is guessing — it depends on your insulation, your ducts, your roof colour and your thermostat. We would rather fix the insulation and the intake first, because those do more.",
  },
  {
    q: "Is more ventilation always better?",
    a: roofline.ventWarning,
  },
  {
    q: "Do I need gutters in Phoenix?",
    a: "Plenty of Valley homes do without them. The ones that need them are the ones where roof runoff lands somewhere that matters — against a foundation, onto a walkway that ices in a cold snap, or down a stucco wall it is slowly staining. We will tell you honestly whether yours is one of them.",
  },
  {
    q: "Is this work under the roofing warranty?",
    a: `Ventilation and skylight work done as part of a roof is covered by the same ${warranty.laborYears || "25"}-year workmanship warranty as the roof. Insulation, gutters and sheet metal carry their own written workmanship term, stated in your contract before anyone starts.`,
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "Attic Insulation, Ventilation, Gutters & Skylights",
      serviceType: "Attic and roofline services",
      provider: { "@type": "RoofingContractor", name: brand.name, url: SITE_URL },
      areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, AZ" },
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Attic & Roofline", item: `${SITE_URL}/attic-roofline` },
      ],
    },
  ],
};

const Plate = ({ items }) => (
  <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
    <div className="grid sm:grid-cols-2 gap-px">
      {items.map((c) => {
        const Icon = icons[c.icon] || Wrench;
        return (
          <div key={c.title} className="bg-card flex gap-5 p-7 sm:p-8">
            <span className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
              <Icon size={18} className="text-brand-dark" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-lg uppercase tracking-wide mb-1.5">{c.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{c.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Band = ({ id, eyebrow, title, lead, items, dark }) => (
  <section id={id} className={`py-24 scroll-mt-28 ${dark ? "bg-secondary" : "bg-background"}`}>
    <div className="max-w-5xl mx-auto px-5 lg:px-8">
      <span className="kicker">{eyebrow}</span>
      <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{title}</h2>
      <div className="brand-line mb-5" />
      <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">{lead}</p>
      <Plate items={items} />
    </div>
  </section>
);

const AtticRoofline = () => {
  // Insulation (R-40) and gutters and sheet metal (R-45) are not roofing, so
  // they ride on the B scope. Gated with remodeling and painting accordingly.
  if (!roofline.enabled) return <Navigate to="/services" replace />;

  return (
    <div>
      <SEO
        title="Attic Insulation, Ventilation, Gutters & Skylights | Phoenix | Pepla Roofing"
        description="Attic insulation, solar attic vents, seamless gutters, custom sheet metal and skylight replacement across Phoenix and Maricopa County — done while the roof is already open."
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="While We Are Up There"
        title="Attic & Roofline"
        subtitle="The work that costs a fraction when it happens during a roof, and full price when it does not."
        image={heroImage}
        imageAlt="Phoenix home roofline"
      />

      {/* Jump row — four distinct jobs, people arrive wanting one */}
      <section className="pt-12 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <nav aria-label="Sections" className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/45">
              Jump to
            </span>
            {[
              ["insulation", "Insulation"],
              ["ventilation", "Ventilation & Solar Vents"],
              ["gutters", "Gutters & Sheet Metal"],
              ["daylight", "Skylights & Solar Tubes"],
            ].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink hover:border-brand hover:text-brand-dark transition-colors"
              >
                {label} <ArrowRight size={13} aria-hidden="true" />
              </a>
            ))}
          </nav>
        </div>
      </section>

      <Band
        id="insulation"
        eyebrow="R-40"
        title="Attic Insulation"
        lead={`A Phoenix attic runs past 150°F on a July afternoon, and the only thing between it and your ceiling is insulation depth. Current code for this climate zone asks for ${roofline.codeRValue}; most Valley homes built before the mid-2000s are nowhere near it.`}
        items={roofline.insulation}
      />

      <Band
        id="ventilation"
        eyebrow="Roofing"
        title="Ventilation & Solar Vents"
        lead="Ventilation is the exclusion written into every manufacturer warranty we publish on this site. It is also the cheapest thing on this page and the one that protects everything else."
        dark
        items={roofline.ventilation}
      />

      {/* The thing a ventilation salesman will not tell you */}
      <section className="pb-24 bg-secondary">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-ink/15 bg-card px-7 py-7 sm:px-8 flex gap-4">
            <AlertTriangle size={21} className="text-brand-dark shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <h3 className="font-heading text-lg uppercase tracking-wide mb-2">
                More vents is not the same as better ventilation
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{roofline.ventWarning}</p>
              <Link
                to="/understanding-your-warranty"
                className="mt-4 inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline"
              >
                What the manufacturers exclude <ArrowRight size={13} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Band
        id="gutters"
        eyebrow="R-45"
        title="Gutters & Sheet Metal"
        lead="Formed on site and set as part of the roof. In the Valley the question is not whether every house needs gutters — it is whether yours is one of the houses where the runoff is landing somewhere it should not."
        items={roofline.gutters}
      />

      <Band
        id="daylight"
        eyebrow="Roofing"
        title="Skylights & Solar Tubes"
        lead="A hole in a roof is a roofing problem, which is why the companies that sell skylights are rarely the ones you want flashing them."
        dark
        items={roofline.daylight}
      />

      {/* FAQ */}
      <section className="py-24 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Questions People Ask
          </h2>
          <div className="brand-line mb-9" />
          <dl className="divide-y divide-ink/10">
            {FAQ.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
                <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Estimate */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-4xl uppercase tracking-tight mb-4">Add It To The Roof</h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed mb-6">
              If a roof is already booked, say so and we will price these alongside it. If you only
              want one of them, that is fine too — we will come look and tell you whether it is
              worth doing on its own.
            </p>
            <p className="text-white/70 leading-relaxed mb-8">
              Either way you get photographs, the ventilation calculation and the insulation depth
              in writing, in the same file as the rest of your roof.
            </p>
            <a
              href={`tel:${brand.phoneRaw}`}
              className="inline-flex items-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
            >
              <Phone size={18} /> {brand.phone}
            </a>
            <p className="text-white/45 text-sm mt-8">
              Looking at a full roof?{" "}
              <Link to="/services/roofing" className="text-brand font-semibold link-underline">
                Roofing services <ArrowRight size={13} className="inline" />
              </Link>
            </p>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-card text-ink border border-ink/10 rounded-2xl p-6 md:p-8 shadow-sm">
              <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Request An Estimate</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Tell us which of these you are thinking about and a team member will call.
              </p>
              <LeadForm defaultService="Attic & Roofline" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AtticRoofline;
